import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Administration Module Service Stores
 */
export const administrationStores: ServiceStoreConfig[] = [
  // `serviceView` ON — the auto-generated raw grid at
  // /management/data/departments, in the Management nav group (see
  // views/utils/ServiceViewRouteGenerator.ts). `manufacturing_departments` is
  // the smallest, flattest reference table in the app (id / name / description
  // / isActive), the only thing pointing at it is `users.departmentId`, and the
  // API gates every write behind `requireAdmin()` and records it via
  // `auditLog('departments')` — so the blast radius of a bad row is a dangling
  // picker entry, not corrupted business data.
  //
  // NOTE — this is the ONE enabled service whose generic "Create New Entity"
  // dialog actually functions: `departments` has a `dataSchema` in the API's
  // api-services service-registry, so the dialog renders name/description/
  // isActive and can POST a real row. Inline edit and delete stay OFF (no
  // `actions` block). The hand-built Departments page remains the primary
  // surface; this one is the raw/export view.
  {
    name: "departments",
    options: {
      // No offline copy, and every find replaces the cache (#2654). Both of
      // this store's readers ask for the whole table (DepartmentsStore,
      // UserManagementStore's picker), so a department removed on the server
      // leaves on the next read. With `enableStorage: true` it never did: the
      // library's offline mirror is written by every find and never pruned,
      // and its hydration adds back any row memory does not hold, so a removed
      // row came back on every reload. See replaceOnFind.contract.test.ts.
      enableStorage: false,
      replaceOnFind: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: true,
        // Item 63 · A leading 160px `id` column spent 42% of the visible
        // 379px on a GUID before the department NAME — the one thing a reader
        // is looking for. The id is still available (it is in the row and in
        // the entity dialog), it just stops being the first thing. With
        // `description` trimmed 320 → 200 the whole view fits an iPad.
        columnHeaders: [
          { key: "name", title: "Name", dataType: "string", width: 220 },
          { key: "description", title: "Description", dataType: "string", width: 200 },
          { key: "isActive", title: "Active", dataType: "boolean", width: 90 },
          { key: "createdAt", title: "Created", dataType: "date", width: 140 },
          { key: "updatedAt", title: "Updated", dataType: "date", width: 140 },
          { key: "id", title: "ID", dataType: "string", width: 160 },
        ],
        defaultSort: {
          sortBy: ["name"],
          sortDirection: { name: "ASC" },
        },
      },
    },
  },
  // The client-assignment matrix (Administration → Client Access). Admin-only
  // screen; find() resolves a BARE ARRAY of the whole matrix. replaceOnFind so a
  // removed row leaves the cache on the next find (see replaceOnFind.contract);
  // no offline copy — this is access information about other people.
  //
  // throwOnError ON, unlike most stores here: with the default a refused
  // grant/confirm/remove resolves null, and the screen said "Access granted"
  // for a grant the API had refused (Codex review 2026-09-25). One view reads
  // this store, and ClientAccessStore catches every call.
  {
    name: "staff-client-assignments",
    options: { idField: "id", enableStorage: false, replaceOnFind: true, throwOnError: true },
  },
  // Which git email is whose (Administration → Git Identities). Admin-only;
  // find() resolves a BARE ARRAY. Same reasoning as the matrix above:
  // replaceOnFind so a removed row leaves the cache, no offline copy, and
  // throwOnError so a refused add (a 409 naming who holds the email) reaches
  // GitIdentitiesStore's catch instead of reading as success.
  {
    name: "developer-git-identities",
    options: { idField: "id", enableStorage: false, replaceOnFind: true, throwOnError: true },
  },
];
